import ApiService from "../../services/ApiService";
import translate from "../../services/Translate";

export const COLOR_PALETTE = [
  "var(--color-blue)",
  "var(--color-green)",
  "var(--color-dark-red)",
  "var(--color-dark-green)",
  "var(--color-electric-blue)",
  "var(--color-orange)",
  "var(--color-pink)",
  "var(--color-violet)",
  "var(--color-dark-brown)",
];

export const sortCohortsWithDeletedLast = (cohorts: { id: string; name: string }[]) => {
  return cohorts.filter(Boolean).sort((a, b) => {
    const aHasName = a.name && a.name.trim() !== "";
    const bHasName = b.name && b.name.trim() !== "";

    if (aHasName && !bHasName) return -1;
    if (!aHasName && bHasName) return 1;
    if (aHasName && bHasName) return a.name.localeCompare(b.name);
    return String(a.id).localeCompare(String(b.id));
  });
};

export const fetchQuestions = (
  surveyTypeQuestionId: string,
  setSurveyQuestions: Function,
  setSurveyQuestionsSpinner: Function
) => {
  ApiService.get(`surveys/types/questions/${surveyTypeQuestionId}`).then((response) => {
    setSurveyQuestions(response.data.survey_questions);
    setSurveyQuestionsSpinner(false);
  });
};

export const fetchAnswer = (
  surveyQuestionId: string,
  setSurveyAnswer: Function,
  setSurveyAnswerSpinner: Function,
  filters?: Record<string, string>
) => {
  let url = `surveys/questions/${surveyQuestionId}/answers`;

  if (filters && Object.keys(filters).length > 0) {
    const params = new URLSearchParams();
    if (filters.cohort_id) params.append("cohort_ids", filters.cohort_id);
    if (filters.cohort_program) params.append("program_names", filters.cohort_program);
    if (filters.project_id) params.append("project_ids", filters.project_id);
    if (filters.meeting_id) params.append("meeting_ids", filters.meeting_id);
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
  } else {
    url += `?fetch_cohort_name=true`;
  }

  ApiService.get(url).then((response) => {
    const answers = response.data.survey_answers.map((answer: any) => ({
      ...answer,
      content: answer.content,
    }));
    setSurveyAnswer(answers);
    setSurveyAnswerSpinner(false);
  });
};

export const getChartData = (surveyAnswer: any, surveyType: any) => {
  if (!surveyAnswer || surveyAnswer.length === 0) return [];

  const responseCounts: { [key: string]: number } = surveyAnswer.reduce(
    (acc: { [key: string]: number }, answer: any) => {
      const response = answer.content.answer;
      acc[response] = (acc[response] || 0) + 1;
      return acc;
    },
    {}
  );

  const totalResponses = Object.values(responseCounts).reduce((sum, count) => sum + count, 0);

  let chartData = Object.entries(responseCounts).map(([response, count], index) => ({
    response,
    count,
    percentage: ((count / totalResponses) * 100).toFixed(2),
    color: COLOR_PALETTE[index % COLOR_PALETTE.length],
  }));

  chartData = chartData.sort((a, b) => parseFloat(a.response) - parseFloat(b.response));

  if (surveyType === "question") {
    chartData = chartData.slice(0, 10);
  }

  return chartData;
};

export const calculateGlobalNote = (surveyAnswer: any) => {
  if (!surveyAnswer.length) return 0;

  const total = surveyAnswer.reduce((sum: number, answer: any) => sum + parseFloat(answer.content.answer || 0), 0);
  return (total / surveyAnswer.length).toFixed(2);
};

export const downloadSurveyAnswersCsv = (
  surveyLanguage: string,
  surveyType: string,
  language: string,
  setIsCsvSurveyDownloading: Function,
  dateFrom?: string,
  dateTo?: string,
  getMeetingStartDate?: boolean
) => {
  setIsCsvSurveyDownloading(true);

  let url = `surveys/${surveyType}/${surveyLanguage}/answers/download?get_meeting_dt_start=${getMeetingStartDate}`;
  const queryParams = new URLSearchParams();

  if (dateFrom) queryParams.append("date_from", dateFrom);
  if (dateTo) queryParams.append("date_to", dateTo);

  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }

  ApiService.get(url)
    .then((resp) => {
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(
        new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      );
      link.setAttribute("download", `${surveyType}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    })
    .catch((err) => {
      console.error(err);
      alert(translate(language, "PAGES.INSIGHTS.SURVEY.DOWNLOADING_CSV_ERROR"));
    })
    .finally(() => setIsCsvSurveyDownloading(false));
};

export const updateFilters = (
  field: string,
  values: string[],
  setActiveFilters: Function,
  surveySelection: Array<any>,
  setSurveyAnswerSpinner: Function,
  surveyQuestions: Array<any>,
  setSurveyAnswer: Function
) => {
  setActiveFilters((activeFilters: Record<string, string>) => {
    const newFilters = {
      ...activeFilters,
      [field]: values.join(",") || "",
    };

    if (surveySelection[0]) {
      setSurveyAnswerSpinner(true);
      const selectedQuestion = surveyQuestions.find((survey) => survey.text === surveySelection[0].label);
      if (selectedQuestion) {
        fetchAnswer(selectedQuestion.question_id, setSurveyAnswer, setSurveyAnswerSpinner, newFilters);
      }
    }

    return newFilters;
  });
};

export const resetAndFetchSurveyData = (
  setActiveFilters: Function,
  surveySelection: Array<any>,
  surveyQuestions: Array<any>,
  setSurveyAnswerSpinner: Function,
  setSurveyAnswer: Function,
  setFilterData: Function
) => {
  setActiveFilters({});
  if (surveySelection[0]) {
    const selectedQuestion = surveyQuestions.find((survey) => survey.text === surveySelection[0].label);
    if (selectedQuestion) {
      setSurveyAnswerSpinner(true);
      setSurveyAnswer([]);
      setFilterData({
        projects: [],
        cohort_names: [],
        cohort_programs: [],
        meetings: [],
      });

      fetchAnswer(
        selectedQuestion.question_id,
        (answers: any[]) => {
          setSurveyAnswer(answers);
          setFilterData({
            projects: Array.from(new Set(answers.map((answer) => answer.project_id))),
            cohort_names: Array.from(new Set(answers.map((answer) => answer.cohort_id))).map((id) => ({
              id,
              name: answers.find((answer) => answer.cohort_id === id)?.cohort_name || "",
            })),
            cohort_programs: Array.from(new Set(answers.map((answer) => answer.cohort_program))),
            meetings: Array.from(new Set(answers.map((answer) => answer.meeting_id))).map((id) => ({
              id,
              name: answers.find((answer) => answer.meeting_id === id)?.meeting_name || "",
            })),
          });
        },
        setSurveyAnswerSpinner
      );
    }
  }
};
